import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AvailabilityKey, DashboardFilterKey, EngineTypeKey, StatusKey } from '../../planning/types';

const DASHBOARD_FILTER_KEY = 'planly_dashboard_filter';
const DASHBOARD_FILTER_MACHINE_IDS_KEY = 'planly_dashboard_filter_machine_ids';
const FILTERS_KEY = 'planly_filters';

function setFilters(filters: Record<string, unknown>) {
  try {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  } catch {
    /* ignore */
  }
}

export function useDashboardNavigation(gridViewStart: string) {
  const navigate = useNavigate();

  const navigateWithAvailability = useCallback(
    (key: AvailabilityKey) => {
      setFilters({
        availability: [key],
        status: ['to_order', 'ordered', 'collected'],
        sites: [],
        pickup: ['confirmed', 'not_confirmed'],
        search: '',
      });
      navigate('/planning', { state: { viewStart: gridViewStart } });
    },
    [navigate, gridViewStart]
  );

  const navigateWithStatus = useCallback(
    (key: StatusKey) => {
      setFilters({
        availability: ['available', 'in_use', 'inactive'],
        status: [key],
        sites: [],
        pickup: ['confirmed', 'not_confirmed'],
        search: '',
      });
      navigate('/planning', { state: { viewStart: gridViewStart } });
    },
    [navigate, gridViewStart]
  );

  const navigateWithEngineType = useCallback(
    (key: EngineTypeKey) => {
      setFilters({
        availability: ['available', 'in_use', 'inactive'],
        status: ['to_order', 'ordered', 'collected'],
        engineType: [key],
        sites: [],
        pickup: ['confirmed', 'not_confirmed'],
        search: '',
      });
      navigate('/planning', { state: { viewStart: gridViewStart } });
    },
    [navigate, gridViewStart]
  );

  const navigateWithCategory = useCallback(
    (category: string) => {
      setFilters({
        availability: ['available', 'in_use', 'inactive'],
        status: ['to_order', 'ordered', 'collected'],
        categories: [category],
        sites: [],
        pickup: ['confirmed', 'not_confirmed'],
        search: '',
      });
      navigate('/planning', { state: { viewStart: gridViewStart } });
    },
    [navigate, gridViewStart]
  );

  const navigateWithSite = useCallback(
    (siteId: string) => {
      setFilters({
        availability: ['available', 'in_use', 'inactive'],
        status: ['to_order', 'ordered', 'collected'],
        sites: [siteId],
        pickup: ['confirmed', 'not_confirmed'],
        search: '',
      });
      navigate('/planning', { state: { viewStart: gridViewStart } });
    },
    [navigate, gridViewStart]
  );

  const navigateWithRentalExtension = useCallback(
    (machineIds: string[]) => {
      try {
        localStorage.setItem(DASHBOARD_FILTER_KEY, 'rental_extension');
        localStorage.setItem(DASHBOARD_FILTER_MACHINE_IDS_KEY, JSON.stringify(machineIds));
      } catch {
        /* ignore */
      }
      navigate('/planning', { state: { viewStart: gridViewStart } });
    },
    [navigate, gridViewStart]
  );

  const navigateWithOverdueReturn = useCallback(
    (machineIds: string[]) => {
      try {
        localStorage.setItem(DASHBOARD_FILTER_KEY, 'overdue_return');
        localStorage.setItem(DASHBOARD_FILTER_MACHINE_IDS_KEY, JSON.stringify(machineIds));
      } catch {
        /* ignore */
      }
      navigate('/planning', { state: { viewStart: gridViewStart } });
    },
    [navigate, gridViewStart]
  );

  const navigateWithDashboardFilter = useCallback(
    (filterKey: Exclude<DashboardFilterKey, 'rental_extension' | 'overdue_return'>) => {
      try {
        localStorage.setItem(DASHBOARD_FILTER_KEY, filterKey);
      } catch {
        /* ignore */
      }
      navigate('/planning', { state: { viewStart: gridViewStart } });
    },
    [navigate, gridViewStart]
  );

  return {
    navigateWithAvailability,
    navigateWithStatus,
    navigateWithEngineType,
    navigateWithCategory,
    navigateWithSite,
    navigateWithRentalExtension,
    navigateWithOverdueReturn,
    navigateWithDashboardFilter,
  };
}
